import { useMemo, useState } from "react";
import { ArrowRight, CheckCircle2, Loader2, MapPin, Sparkles } from "lucide-react";
import { PRIMARY_TOUR, UNLOCK_TOUR } from "@/data/tourSchedule";

const TOUR_CITIES = [...PRIMARY_TOUR, ...UNLOCK_TOUR].map(({ city }) => city);
const MONTHS = Array.from({ length: 12 }, (_, index) => ({
  value: "2027-" + String(index + 1).padStart(2, "0"),
  label: new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(Date.UTC(2027, index, 1)))
}));
const SLOTS = ["Friday evening", "Saturday daytime", "Saturday evening", "Sunday daytime", "Flexible"];
const MAX_TEXT = 120;

export function monthWeekends(month) {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(month)) return [];
  const [year, monthNumber] = month.split("-").map(Number);
  const count = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
  const weekends = [];
  const dateFormat = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
  for (let day = 1; day <= count; day++) {
    const friday = new Date(Date.UTC(year, monthNumber - 1, day));
    if (friday.getUTCDay() !== 5) continue;
    const sunday = new Date(friday.getTime() + 2 * 86400000);
    weekends.push({
      value: friday.toISOString().slice(0, 10) + "/" + sunday.toISOString().slice(0, 10),
      label: dateFormat.format(friday) + "–" + dateFormat.format(sunday)
    });
  }
  return weekends;
}

function initialSelection(defaultCity, locked) {
  if (locked || typeof window === "undefined") return defaultCity;
  const requested = new URLSearchParams(window.location.search).get("voteCity");
  if (!requested) return defaultCity;
  return TOUR_CITIES.find((name) => name.toLowerCase() === requested.toLowerCase()) || defaultCity;
}

export default function TourDateVote({ initialCity = "Houston", lockCity = false }) {
  const [city, setCity] = useState(() => initialSelection(initialCity, lockCity));
  const [otherCity, setOtherCity] = useState("");
  const [month, setMonth] = useState("");
  const [weekend, setWeekend] = useState("Any weekend");
  const [slot, setSlot] = useState("Saturday evening");
  const [attendance, setAttendance] = useState("Likely to attend");
  const [emailOptIn, setEmailOptIn] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const weekends = useMemo(() => monthWeekends(month), [month]);
  const resolvedCity = city === "Other" ? otherCity.trim() : city;
  const control = "min-h-12 w-full rounded-xl border border-white/20 bg-[#181818] px-4 text-sm text-[#F5F0E8] outline-none transition focus:border-[#E32636] focus:ring-2 focus:ring-[#E32636]/20";
  const fieldLabel = "mb-2 block font-accent text-[11px] tracking-[.12em] text-white/70";

  async function sendVote(event) {
    event.preventDefault();
    if (busy) return;
    setError("");
    setStatus("");
    if (resolvedCity.length < 2 || resolvedCity.length > 90) {
      setError("Choose a city or enter a city name.");
      return;
    }
    if (!month) {
      setError("Choose the month that works best for you.");
      return;
    }
    if (emailOptIn && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError("Enter a valid email to receive early access updates.");
      return;
    }
    const query = new URLSearchParams(window.location.search);
    const track = (key) => (query.get(key) || "").slice(0, MAX_TEXT);
    const params = new URLSearchParams(new FormData(event.currentTarget));
    params.set("form-name", "pole-tour-date-vote-2027");
    params.set("city", resolvedCity);
    params.set("preferred_month", month);
    params.set("preferred_weekend", weekend);
    params.set("day_preference", slot);
    params.set("attendance_intent", attendance);
    params.set("email_opt_in", emailOptIn ? "yes" : "no");
    params.set("email", emailOptIn ? email.trim().toLowerCase() : "");
    params.set("ref", track("ref"));
    params.set("utm_source", track("utm_source"));
    params.set("utm_medium", track("utm_medium"));
    params.set("utm_campaign", track("utm_campaign"));
    params.set("utm_content", track("utm_content"));
    params.set("landing_path", window.location.pathname);

    setBusy(true);
    try {
      const response = await fetch("/__forms.html", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString()
      });
      if (!response.ok) throw new Error("Vote submission was not accepted.");
      setStatus("Your vote for " + resolvedCity + " is in. Thank you for helping shape the tour!");
      setMonth("");
      setWeekend("Any weekend");
      setEmail("");
      setEmailOptIn(false);
    } catch {
      setError("We couldn't record your vote. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section id="vote" className="scroll-mt-24 border-y border-white/10 bg-[#0b0b0d] px-4 py-16 text-[#F5F0E8] sm:px-5 md:py-24" aria-labelledby="tour-vote-title">
      <div className="mx-auto grid max-w-7xl overflow-hidden rounded-[2rem] border border-white/15 bg-[#111113] shadow-[0_28px_100px_rgba(0,0,0,.35)] lg:grid-cols-[.88fr_1.12fr]">
        <div className="relative min-h-[370px] overflow-hidden border-b border-white/10 lg:min-h-[660px] lg:border-b-0 lg:border-r">
          <img src="/campaign-assets/party-red.webp" alt="Editorial Pole & Polaroids experience with dramatic red lighting" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-55" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#090909] via-[#090909]/65 to-[#250609]/25" />
          <div className="relative flex h-full flex-col justify-between gap-10 p-7 sm:p-10">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full border border-white/30 bg-black/50 px-4 py-2 font-accent text-[10px] tracking-[.18em]">2027 TOUR SURVEY</span>
              <span className="rounded-full border border-[#39E75F]/50 bg-black/50 px-4 py-2 font-accent text-[10px] tracking-[.12em] text-[#39E75F]">VOTING OPEN</span>
            </div>
            <div>
              <p className="mb-4 font-accent text-xs tracking-[.25em] text-[#F5F0E8]/65">FOTOS ON THE GEAUX ASKS</p>
              <h2 id="tour-vote-title" className="font-display text-[clamp(3.8rem,7vw,6.3rem)] leading-[.87] tracking-[-.05em]">YOUR CITY.<br /><span className="italic text-[#FF6D77]">YOUR DATE.</span></h2>
              <p className="mt-6 max-w-md text-sm leading-7 text-white/80 md:text-base">We're bringing the poles, the flash and the Polaroids. You tell us when to pull up. Your answers help shape the 2027 route.</p>
              <p className="mt-6 flex items-center gap-2 font-accent text-xs tracking-[.12em] text-[#39E75F]"><MapPin size={16} /> HOUSTON + CHICAGO ARE FOUNDING CITIES</p>
              <p className="mt-3 text-xs leading-5 text-white/55">This is audience research, not a ticket reservation. Stops, venues and dates are not confirmed.</p>
            </div>
          </div>
        </div>
        <form name="pole-tour-date-vote-2027" method="POST" data-netlify="true" netlify-honeypot="bot-field" onSubmit={sendVote} className="grid min-w-0 content-start gap-6 p-6 sm:p-9 md:p-11" aria-label="Vote on Pole and Polaroids tour dates">
          <input type="hidden" name="form-name" value="pole-tour-date-vote-2027" />
          <div aria-hidden="true" style={{ position: "absolute", left: "-10000px" }}>
            <label>Leave this blank<input type="text" name="bot-field" tabIndex={-1} autoComplete="off" /></label>
          </div>
          <div>
            <p className="font-accent text-[11px] tracking-[.2em] text-[#E32636]">TELL US WHERE + WHEN</p>
            <h3 className="mt-2 font-display text-4xl leading-none sm:text-5xl">Help us pick the weekend.</h3>
            <p className="mt-3 text-sm leading-6 text-white/55">A minute of your time helps us make the next stop happen.</p>
          </div>
          <div className="grid gap-3">
            <label htmlFor="vote-city" className={fieldLabel}><span className="text-[#E32636]">01 /</span> YOUR CITY *</label>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Founding city quick select">
              {["Houston", "Chicago"].map((name) => (
                <button key={name} type="button" disabled={lockCity} onClick={() => { setCity(name); setStatus(""); }} aria-pressed={city === name} className={"min-h-11 rounded-full border px-5 font-accent text-xs tracking-[.12em] transition disabled:cursor-default " + (city === name ? "border-[#E32636] bg-[#E32636] text-white" : "border-white/25 text-white/70 hover:border-[#E32636]")}>{name.toUpperCase()}</button>
              ))}
            </div>
            <select id="vote-city" name="city" className={control} value={city} disabled={lockCity} onChange={(event) => { setCity(event.target.value); setStatus(""); }}>
              {TOUR_CITIES.map((name) => <option key={name} value={name}>{name}</option>)}
              {!lockCity && <option value="Other">Another city — let us know</option>}
            </select>
            {lockCity && <input type="hidden" name="city" value={city} />}
            {city === "Other" && <label className="grid gap-2 text-xs text-white/70">CITY + STATE *
              <input className={control} required maxLength={90} value={otherCity} onChange={(event) => setOtherCity(event.target.value)} placeholder="e.g. Charlotte, NC" />
            </label>}
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="vote-month" className={fieldLabel}><span className="text-[#E32636]">02 /</span> BEST MONTH *</label>
              <select id="vote-month" name="preferred_month" className={control} required value={month} onChange={(event) => { setMonth(event.target.value); setWeekend("Any weekend"); }}>
                <option value="">Choose a month</option>
                {MONTHS.map((item) => <option value={item.value} key={item.value}>{item.label}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="vote-weekend" className={fieldLabel}>PREFERRED WEEKEND</label>
              <select id="vote-weekend" name="preferred_weekend" className={control} value={weekend} disabled={!month} onChange={(event) => setWeekend(event.target.value)}>
                <option value="Any weekend">Any weekend that month</option>
                {weekends.map((item) => <option value={item.value} key={item.value}>{item.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label htmlFor="vote-day" className={fieldLabel}>BEST TIME OF THE WEEKEND</label>
            <select id="vote-day" name="day_preference" className={control} value={slot} onChange={(event) => setSlot(event.target.value)}>
              {SLOTS.map((value) => <option key={value} value={value}>{value}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="vote-intent" className={fieldLabel}>IF WE COME THROUGH...</label>
            <select id="vote-intent" name="attendance_intent" className={control} value={attendance} onChange={(event) => setAttendance(event.target.value)}>
              <option value="Likely to attend">I'm likely to attend</option>
              <option value="Interested">I'm interested — send the details</option>
              <option value="Supporter">Just showing my city some love</option>
            </select>
          </div>
          <div className="rounded-2xl border border-white/10 bg-black/25 p-4 sm:p-5">
            <label className="flex cursor-pointer items-start gap-3 text-sm leading-6 text-white/75">
              <input name="email_opt_in" type="checkbox" value="yes" checked={emailOptIn} onChange={(event) => { setEmailOptIn(event.target.checked); if (!event.target.checked) setEmail(""); }} className="mt-1 h-4 w-4 shrink-0 accent-[#E32636]" />
              <span><strong className="font-semibold text-white">Give me first dibs.</strong> Send me occasional email updates and early access if my city is selected. Optional — I can vote without joining a mailing list.</span>
            </label>
            {emailOptIn && <label htmlFor="vote-email" className="mt-4 block"><span className={fieldLabel}>EMAIL FOR EARLY ACCESS *</span>
              <input id="vote-email" name="email" type="email" autoComplete="email" className={control} maxLength={254} placeholder="you@example.com" required value={email} onChange={(event) => setEmail(event.target.value)} />
            </label>}
            <p className="mt-3 text-[11px] leading-5 text-white/40">Your email is used only if you opt in. Unsubscribe any time. Voting does not reserve a ticket, and we won't send texts or DMs from this form.</p>
          </div>
          <div aria-live="polite" className="text-sm">
            {status && <p className="flex items-start gap-2 rounded-xl border border-[#39E75F]/35 bg-[#39E75F]/10 p-3 text-[#C2FFD2]"><CheckCircle2 size={19} className="mt-0.5 shrink-0" />{status}</p>}
            {error && <p role="alert" className="rounded-xl border border-[#E32636]/50 bg-[#E32636]/10 p-3 text-[#FFD2D6]">{error}</p>}
          </div>
          <button type="submit" disabled={busy} className="flex min-h-14 w-full items-center justify-center gap-3 rounded-full bg-[#E32636] px-6 font-accent text-sm tracking-[.17em] text-white transition hover:bg-[#fb4151] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#E32636] disabled:cursor-wait disabled:opacity-60">
            {busy ? <><Loader2 size={18} className="animate-spin" /> SUBMITTING...</> : <>SUBMIT MY VOTE <ArrowRight size={18} /></>}
          </button>
          <p className="flex items-center justify-center gap-2 text-center text-[11px] text-white/45"><Sparkles size={13} /> Made with love in New Orleans · Fotos on the Geaux</p>
        </form>
      </div>
    </section>
  );
}
